/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import useResizableColumns from '../../hooks/useResizableColumns';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDateSafe } from '../../utils/formatUtils';
import '../../styles/Projects.css';

const ExtendRevise = () => {
  const [applications, setApplications] = useState([]);

  // Drag the dividers in the header to set column widths; the browser
  // remembers them per person.
  const tableRef = useResizableColumns('user-extend-revise');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get('/api/applications/saved');
      const revisedApps = response.data.filter(app => app.project_status === 'Revision Required');
      setApplications(revisedApps);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to fetch applications.');
      setLoading(false);
    }
  };

  const handleView = (application) => {
    navigate(`/applications/view/${application._id}`, { state: { application } });
  };

  const handleEdit = (application) => {
    navigate(`/applications/edit/${application._id}`, { 
      state: { 
        application: {
          ...application,
          project_start_date: formatDateSafe(application.project_start_date),
          project_end_date: formatDateSafe(application.project_end_date)
        } 
      } 
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="extend-revise-container">
      <h1>Applications Needing Revision</h1>
      {applications.length === 0 ? (
        <p>No applications require revision at this time.</p>
      ) : (
        <div className="applications-table">
          <table ref={tableRef} className="resizable-table">
            <thead>
              <tr>
                <th className="col-project-title">Project Title</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Admin Comments</th>
                <th>Reviewed By</th>
                <th>Review Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app._id}>
                  <td className="col-project-title">{app.project_title || 'Untitled'}</td>
                  <td>{formatDateSafe(app.project_start_date)}</td>
                  <td>{formatDateSafe(app.project_end_date)}</td>
                  <td>
                    {app.comments && app.comments.length > 0
                      ? app.comments
                          .filter(comment => 
                            comment.comment_type === 'revision_request' || 
                            comment.comment_type === 'rejection' ||
                            comment.comment_type === 'general'
                          )
                          .slice(-1)[0]?.comment || 'No relevant comments'
                      : 'No comments'}
                  </td>
                  <td>{app.reviewed_by || '-'}</td>
                  <td>{formatDateTime(app.reviewed_at)}</td>
                  <td className="actions-cell">
                    <button onClick={() => handleView(app)}>View</button>
                    <button onClick={() => handleEdit(app)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExtendRevise;
